import React from 'react';
import { Checkbox } from '@mui/material';
import {
    goalDataTypes,
    Images,
    PermissionList,
    renderGoalTypes,
    RolePermission,
    tagEnums,
    renderTargetStatuses,
    targetStatusEnums,
    isMaintenanceRunning,
    isMaintenanceCompleted,
    maintenanceBadges,
} from 'utils';
import {
    ActionLoaded,
    DateRow,
    EditWithIcon,
    FilterTags,
    LocalDateRow,
    MoreActions,
    NameAndFolder,
    SimpleTooltip,
    SkillCard,
    StatusCard,
    TagComponent,
    TextRow,
} from 'components';
import { GoalStatusBox } from './goalStatusBox';
import { canEditTargetStatus, GoalTargetStatus, isTargetStatusLocked, MasteredTargetTooltip } from './targets/goalTargetStatus';
import { MaintenanceInfo } from './targets/maintenanceInfo';
import { canReadTags } from 'store/tags/tag.utils';
import moment from 'moment/moment';

export { canEditTargetStatus };

export const DELETE_GOAL_ACTION_TYPE = 'DELETE_GOAL';
export const RESTORE_GOAL_ACTION_TYPE = 'RESTORE_GOAL';
export const CREATE_LIBRARY_GOAL_ACTION_TYPE = 'CREATE_LIBRARY_GOAL';

export const goalStatusEnums = {
    PENDING: 'PENDING',
    HOLD: 'HOLD',
    ACTIVE: 'ACTIVE',
    MASTERED: 'MASTERED',
    DISCONTINUED: 'DISCONTINUED',
};

export const goalStatuses = [
    { label: 'Pending', id: goalStatusEnums.PENDING, circleColor: '#B54708', textColor: '#B54708' },
    { label: 'Hold', id: goalStatusEnums.HOLD, circleColor: '#717680', textColor: '#414651' },
    { label: 'Active', id: goalStatusEnums.ACTIVE, circleColor: '#12B76A', textColor: '#027A48' },
    { label: 'Mastered', id: goalStatusEnums.MASTERED, circleColor: '#2E90FA', textColor: '#175CD3' },
    { label: 'Discontinued', id: goalStatusEnums.DISCONTINUED, circleColor: '#F63D68', textColor: '#C01048' },
];

/**
 * Goal list sort keys — mirror of the package's `GoalListSortMap`
 * (`name` / `createdAt`; `creator` is library-only and not an org default).
 */
export const goalSortTypeEnums = {
    NAME: 'name',
    CREATED_AT: 'createdAt',
};

export const sortOrderEnums = {
    ASC: 'ASC',
    DESC: 'DESC',
};

/**
 * Goal orders an org can pick as its default (Settings > Clinical; stored as
 * `clinicalSettings.goalSortType` + `goalOrderType`, default name A→Z) and
 * that the session goal picker offers. `id` is `<sortType>:<orderType>`.
 */
export const goalOrderOptions = [
    { id: `${goalSortTypeEnums.NAME}:${sortOrderEnums.ASC}`, name: 'Name A→Z', sortType: goalSortTypeEnums.NAME, orderType: sortOrderEnums.ASC },
    { id: `${goalSortTypeEnums.NAME}:${sortOrderEnums.DESC}`, name: 'Name Z→A', sortType: goalSortTypeEnums.NAME, orderType: sortOrderEnums.DESC },
    { id: `${goalSortTypeEnums.CREATED_AT}:${sortOrderEnums.DESC}`, name: 'Newest first', sortType: goalSortTypeEnums.CREATED_AT, orderType: sortOrderEnums.DESC },
    { id: `${goalSortTypeEnums.CREATED_AT}:${sortOrderEnums.ASC}`, name: 'Oldest first', sortType: goalSortTypeEnums.CREATED_AT, orderType: sortOrderEnums.ASC },
];

/** The option for a stored sort/order pair; falls back to the default (Name A→Z). */
export const findGoalOrderOption = (sortType, orderType) =>
    goalOrderOptions.find((o) => o.sortType === sortType && o.orderType === orderType) || goalOrderOptions[0];

export const goalTableInfo = (renderParams, domainList, handleAction, expandedGoalIds, showDeleted) => {
    return {
        loadingType: 'GET_GOALS',
        head: [
            { name: 'name', title: 'Goal Name', width: '17%' },
            {
                name: 'domains',
                title: 'Domain',
                disabled: true,
                multiple: true,
                width: '10%',
                filterList: [{ name: 'All', id: 'All' }, ...domainList],
            },
            { name: 'createdAt', title: 'Created', iconType: 'date', disabled: true, width: '7%' },
            { name: 'started', title: 'Started', iconType: 'date', custom: false, disabled: true, width: '7%' },
            {
                name: 'type',
                title: 'Type',
                disabled: true,
                // multiple: true,
                width: '14%',
                filterList: [
                    { name: 'All', id: 'All' },
                    { name: 'Behavior', id: goalTypeEnums.BEHAVIOR },
                    { name: 'Skill', id: goalTypeEnums.SKILL },
                    { name: 'Parent Training', id: goalTypeEnums.PARENT_TRAINING },
                ],
            },
            canReadTags() && { name: '', title: 'Tags', custom: false, renderView: <FilterTags />, width: '12%' },
            { name: 'dataCollection', title: 'Data Method', custom: false, width: '10%' },
            { name: 'mastery', title: 'Mastery %', custom: false, width: '7%' },
            {
                name: 'statuses',
                title: 'Status',
                disabled: true,
                multiple: true,
                width: '10%',
                filterList: [
                    { name: 'All', id: 'All' },
                    { name: 'Hold', id: goalStatusEnums.HOLD },
                    { name: 'Pending', id: goalStatusEnums.PENDING },
                    { name: 'Active', id: goalStatusEnums.ACTIVE },
                    { name: 'Mastered', id: goalStatusEnums.MASTERED },
                    { name: 'Discontinued', id: goalStatusEnums.DISCONTINUED },
                ],
                defaultSelected: [],
            },
            { name: 'firstName', title: 'Action', custom: false, width: '7%' },
        ],

        body: (item) => {
            const lastHistory = item?.statusHistory?.length ? item?.statusHistory[item?.statusHistory?.length - 1] : {};

            return [
                {
                    value: (
                        <div className="full-width">
                            <TextRow name={item?.name} />
                            {item?.deletedBy && item?.deletedAt && (
                                <p className="deleted-by-info">
                                    <TextRow textWidth={7} name={`Deleted by ${item?.deletedBy || '—'} | ${moment(item?.deletedAt).format('MM/DD/YYYY')}`} />
                                    {/*{`Deleted by ${item?.deletedBy || '—'} | ${moment(item?.deletedAt).format('MM/DD/YYYY')}`}*/}
                                </p>
                            )}
                        </div>
                    ),
                    width: '17%',
                },
                {
                    value: (
                        <div className="full-width">
                            <TextRow name={item?.domain?.name} />
                        </div>
                    ),
                    width: '10%',
                },
                {
                    value: (
                        <div className="full-width">
                            <DateRow date={item?.createdAt} />
                        </div>
                    ),
                    width: '7%',
                },
                {
                    value: (
                        <div className="full-width">
                            <DateRow date={lastHistory?.createdAt} />
                        </div>
                    ),
                    width: '7%',
                },
                {
                    value: (
                        <div className="full-width">
                            <SkillCard type={item?.type} />
                        </div>
                    ),
                    width: '14%',
                },
                canReadTags() && {
                    value: <TagComponent userInfo={item} module={tagEnums.GOAL} requestParams={renderParams} allowAdd={!item?.deletedAt} />,
                    width: '12%',
                    notClickable: true,
                },
                { value: <div className="full-width">{renderGoalTypes(item?.dataType)}</div>, width: '10%' },
                {
                    value: (
                        <div className="full-width">
                            <ActionLoaded loadValue={item?.goalProgress?.progressPercent ? item?.goalProgress?.progressPercent : 0} />
                        </div>
                    ),
                    width: '7%',
                },
                {
                    value: (
                        <div className="full-width">
                            <GoalStatusBox info={item} requestParams={renderParams} disabled={!!item?.deletedAt} />
                        </div>
                    ),
                    notClickable: true,
                    width: '10%',
                },
                {
                    value: (
                        <div className="flex-align-center" style={{ gap: 8 }}>
                            {!item?.deletedAt && (
                                <MoreActions
                                    item={item}
                                    handleAction={(e, actionItem, name) => handleAction(actionItem, name)}
                                    buttonsList={[
                                        RolePermission([PermissionList.GOAL_CREATE?.code]) && {
                                            text: 'Duplicate',
                                            icon: Images.duplicate,
                                            actionName: 'clone',
                                        },
                                        RolePermission([PermissionList.GOAL_CREATE?.code]) && {
                                            text: 'Delete',
                                            icon: Images.redTrash,
                                            actionName: 'delete',
                                        },
                                        { text: 'chart', icon: Images.insightsChart, actionName: 'chart' },
                                    ]}
                                />
                            )}
                            {item?.deletedAt && RolePermission([PermissionList.GOAL_CREATE?.code]) && (
                                <MoreActions
                                    item={item}
                                    handleAction={(e, actionItem, name) => handleAction(actionItem, name)}
                                    buttonsList={[{ text: 'Restore', icon: Images.revertIcon, actionName: 'restore' }]}
                                />
                            )}

                            {/*<EditWithIcon icon={Images.insightsChart} handleClick={() => handleAction(item, 'chart')} />*/}
                            <EditWithIcon
                                icon={expandedGoalIds?.has(item?.id) ? Images.dropUp : Images.dropDown}
                                handleClick={() => handleAction(item, 'dropDown')}
                            />
                        </div>
                    ),
                    notClickable: true,
                    width: '7%',
                },
            ];
        },

        renderAccordionRow: (i) => i?.targets?.filter((target) => !target?.deletedAt),

        accordionHead: [
            { title: 'Target Name', width: '25%' },
            { title: 'Objective', width: '35%' },
            { title: 'Progress', width: '10%' },
            { title: 'Last Transitioned', width: '15%' },
            { title: 'Status', width: '10%' },
            { title: 'Action', width: '5%' },
        ],

        accordionBodyCustomClass: 'goal-target-accordion-body',

        accordionBody: (i, j, parent) => {
            const lastHistory = i?.statusHistory?.length ? i?.statusHistory[i?.statusHistory?.length - 1] : {};

            return [
                {
                    value: (
                        <div className="full-width" onClick={(e) => handleAction(parent, 'targetDetail', i)}>
                            <TextRow name={i?.name} />
                        </div>
                    ),
                    width: '25%',
                },
                {
                    value: (
                        <div className="full-width" onClick={(e) => handleAction(parent, 'targetDetail', i)}>
                            <TextRow name={i?.description} />
                        </div>
                    ),
                    width: '35%',
                },
                {
                    value: (
                        <div className="full-width" onClick={(e) => handleAction(parent, 'targetDetail', i)}>
                            {i?.status === targetStatusEnums.TEACHING ? (
                                <>
                                    {i?.progress === -1 ? (
                                        <div>
                                            <SimpleTooltip title={<p>Auto Mastery is currently disabled</p>} placement="top" arrow>
                                                <p>...</p>
                                            </SimpleTooltip>
                                        </div>
                                    ) : (
                                        <ActionLoaded loadValue={i?.progress} />
                                    )}
                                </>
                            ) : isMaintenanceRunning(i) ? (
                                <ActionLoaded
                                    loadValue={i?.maintenanceState?.stepProgress}
                                    circleActiveColor={i?.maintenanceState?.stepProgress < 0 ? '#D92D20' : '#038A4D'}
                                    circlePassiveColor={i?.maintenanceState?.stepProgress < 0 ? '#FEF3F2' : '#E6F5EE'}
                                    stepCount={
                                        i?.maintenance?.followGoalSettings
                                            ? ` ${i?.maintenanceState?.maintenanceStep || 0}/${parent?.maintenance?.steps?.length || 0}`
                                            : ` ${i?.maintenanceState?.maintenanceStep || 0}/${i?.maintenance?.steps?.length || 0}`
                                    }
                                />
                            ) : i?.status === targetStatusEnums.MASTERED ? (
                                <ActionLoaded loadValue={100} />
                            ) : (
                                '...'
                            )}
                        </div>
                    ),
                    width: '10%',
                },
                {
                    // Pencil opens the "Last Transitioned" edit (date + time of the latest status change).
                    value: (
                        <div className="full-width flex-align-center" style={{ gap: 6 }}>
                            <div onClick={(e) => handleAction(parent, 'targetDetail', i)}>
                                <LocalDateRow date={lastHistory?.createdAt} />
                            </div>
                            {lastHistory?.createdAt && !parent?.deletedAt && RolePermission([PermissionList.GOAL_CREATE?.code]) && (
                                <SimpleTooltip title={<p>Edit transition date</p>} placement="top" arrow>
                                    <button
                                        type="button"
                                        className="reset-btn-no-paddings"
                                        style={{ flexShrink: 0 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAction(parent, 'editTransitionDate', i);
                                        }}
                                    >
                                        <img src={Images.edit} alt="edit transition date" style={{ width: 16, height: 16 }} />
                                    </button>
                                </SimpleTooltip>
                            )}
                        </div>
                    ),
                    width: '15%',
                },
                {
                    // Users who can edit targets change the status in place; the click never opens target detail.
                    // A mastered target also shows its maintenance badge and details (maintenance is not a status).
                    value: canEditTargetStatus(parent) ? (
                        <div className="full-width flex-align-center" style={{ gap: 4, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                            <MasteredTargetTooltip target={i}>
                                <GoalTargetStatus
                                    target={i}
                                    goalById={parent}
                                    requestParams={renderParams}
                                    noAction={isTargetStatusLocked(parent)}
                                />
                            </MasteredTargetTooltip>
                            <TargetMaintenanceBadges target={i} goal={parent} />
                        </div>
                    ) : (
                        <div
                            className="full-width flex-align-center"
                            style={{ gap: 4, flexWrap: 'wrap' }}
                            onClick={(e) => handleAction(parent, 'targetDetail', i)}
                        >
                            <MasteredTargetTooltip target={i}>
                                <StatusCard
                                    status={renderTargetStatuses?.find((j) => i?.status === j?.value)?.label}
                                    custom={renderTargetStatuses?.find((j) => i?.status === j?.value)}
                                />
                            </MasteredTargetTooltip>
                            <TargetMaintenanceBadges target={i} goal={parent} />
                        </div>
                    ),
                    notClickable: canEditTargetStatus(parent),
                    width: '10%',
                },
                {
                    value: (
                        <EditWithIcon
                            styles={{ opacity: parent?.deletedAt ? 0.5 : 1 }}
                            handleClick={(e) => handleAction(parent, 'targetChart', i)}
                            icon={Images.insightsChart}
                            type="button"
                        />
                    ),
                    width: '5%',
                },
            ];
        },

        noDetailsText: 'No Targets yet',
        notYet: {
            title: showDeleted ? 'No deleted goals' : 'No Goals Information',
            subTitle: showDeleted ? 'There are no deleted goals to display.' : 'Start by adding your first Goal to manage their information.',
            image: Images.noClientYet,
        },
    };
};

export const GoalStatusRow = ({ status }) => {
    const renderColors = (type) => {
        switch (type) {
            case goalStatusEnums.PENDING:
                return {
                    backgroundColor: 'rgb(255, 250, 235)',
                    circleColor: '#B54708',
                    color: '#B54708',
                    text: 'Pending',
                };
            case goalStatusEnums.ACTIVE:
                return { backgroundColor: '#E6F9F0', circleColor: '#12B76A', color: '#027A48', text: 'Active' };
            case goalStatusEnums.HOLD:
                return { backgroundColor: '#F2F3F5', circleColor: '#717680', color: '#414651', text: 'Hold' };
            case goalStatusEnums.MASTERED:
                return { backgroundColor: '#E6F4FF', circleColor: '#2E90FA', color: '#175CD3', text: 'Mastered' };
            case goalStatusEnums.DISCONTINUED:
                return { backgroundColor: '#FDEDED', circleColor: '#F63D68', color: '#C01048', text: 'Discontinued' };

            default:
                return { backgroundColor: '#F2F3F5', circleColor: '#717680', color: '#414651', text: status };
        }
    };

    const { backgroundColor, circleColor, color, text } = renderColors(status);

    return (
        <div className="status-card-box" style={{ backgroundColor: backgroundColor }}>
            <div style={{ backgroundColor: circleColor }} />
            <p style={{ color: color }}>{text}</p>
        </div>
    );
};

/** How much of a library goal is picked: none, some of its targets, or all of them. */
export const librarySelectionState = (goal, selectedMap) => {
    const picked = selectedMap.get(goal?.id);
    if (!picked) return 'none';
    const total = goal?.targets?.length ?? 0;
    return picked.size === total ? 'all' : 'some';
};

const stopRowToggle = (e) => e.stopPropagation();

export const goalsChooseLibraryTableInfo = ({
    domainList,
    goals,
    selectedMap,
    toggleGoal,
    toggleAll,
    sendParams,
    setSendParams,
    renderParams,
}) => {
    const selectable = (goals ?? []).filter((goal) => !goal?.alreadyAdded);
    const states = selectable.map((goal) => librarySelectionState(goal, selectedMap));
    const allPicked = !!states.length && states.every((state) => state === 'all');
    const somePicked = !allPicked && states.some((state) => state !== 'none');

    return {
        loadingType: 'GET_LIBRARY_GOALS',
        head: [
            {
                renderView: (
                    <SimpleTooltip title={<p>Select All</p>} placement="top-start">
                        <Checkbox
                            onChange={(e) => toggleAll(e.target.checked)}
                            name={'checkBox'}
                            style={{ color: '#0C66E4' }}
                            checked={allPicked}
                            indeterminate={somePicked}
                            disabled={!selectable.length}
                        />
                    </SimpleTooltip>
                ),
                width: '55px',
            },
            { name: 'name', searchKey: 'name', title: 'Goal Name', width: '22%' },
            { name: 'targets', title: 'Targets', custom: false, disabled: true, width: '10%' },
            { name: 'creator', title: 'Creator', disabled: true, width: '13%' },
            {
                name: 'dataTypes',
                title: 'Data Collection Method',
                disabled: true,
                multiple: true,
                filterList: [{ name: 'All', id: 'All' }, ...goalDataTypes.map((goalType) => ({ id: goalType.id, name: goalType.label }))],
                width: '17%',
            },
            {
                name: 'domains',
                rowName: 'name',
                title: 'Domain',
                disabled: true,
                multiple: true,
                filterList: [{ name: 'All', id: 'All' }, ...domainList],
                width: '13%',
            },
            {
                name: 'goalType',
                title: 'Type',
                disabled: true,
                filterList: [{ name: 'All', id: 'All' }, ...goalTypes.map((goal) => ({ id: goal.id, name: goal.label }))],
                width: '10%',
            },
            canReadTags() && {
                name: '',
                title: 'Tags',
                custom: false,
                renderView: <FilterTags sendParams={sendParams} setSendParams={setSendParams} />,
                width: '12%',
            },
        ],

        body: (item) => {
            const state = librarySelectionState(item, selectedMap);
            const total = item?.targets?.length ?? 0;
            const picked = selectedMap.get(item?.id)?.size ?? 0;
            return [
                {
                    value: (
                        <div onClick={stopRowToggle} style={{ padding: 16 }}>
                            <SimpleTooltip title={item?.alreadyAdded ? <p>Already added to this client</p> : ''} placement="top-start">
                                <span>
                                    <Checkbox
                                        onChange={(e) => toggleGoal(item, e.target.checked)}
                                        name={'checkBox'}
                                        style={{ padding: 0, color: item?.alreadyAdded ? undefined : '#0C66E4' }}
                                        checked={state === 'all'}
                                        indeterminate={state === 'some'}
                                        disabled={!!item?.alreadyAdded}
                                    />
                                </span>
                            </SimpleTooltip>
                        </div>
                    ),
                    styles: { width: '55px', padding: 0 },
                },
                {
                    value: <NameAndFolder name={item.name} background={'#E6F5EE'} />,
                    width: '22%',
                },
                { value: <TextRow name={picked ? `${picked} / ${total}` : `${total}`} />, width: '10%' },
                { value: <TextRow name={item?.creator} />, width: '13%' },
                { value: <TextRow name={renderGoalTypes(item?.dataType)} />, width: '17%' },
                { value: <TextRow name={item?.domain?.name} />, width: '13%' },
                { value: <SkillCard type={item?.type} />, width: '10%' },
                canReadTags() && {
                    value: (
                        <div onClick={stopRowToggle}>
                            <TagComponent userInfo={item} module={tagEnums.LIBRARY_GOAL} requestParams={renderParams} allowAdd={false} />
                        </div>
                    ),
                    width: '12%',
                },
            ];
        },

        notYet: goalsChooseLibraryNotYet,
    };
};

export const goalsChooseLibraryNotYet = {
    title: 'No Goals Yet.',
    subTitle: 'Your library is empty. Please create a goal.',
    image: Images.noClientYet,
};

export const goalsChooseLibraryTargetsNotYet = {
    title: 'No Targets.',
    subTitle: 'This library goal has no targets — it is added empty.',
    image: Images.noClientYet,
};

// export const ACTION_TYPE = 'GET_GOALS';
//
// export const goalNotYet = {
//     title: 'No Goals Information',
//     subTitle: 'Start by adding your first Goal to manage their information.',
//     image: Images.noClientYet,
// };

export const goalTypeEnums = {
    BEHAVIOR: 'BEHAVIOR',
    SKILL: 'SKILL',
    PARENT_TRAINING: 'PARENT_TRAINING',
};

export const goalTypes = [
    { label: 'Behavior', id: goalTypeEnums.BEHAVIOR },
    { label: 'Skill', id: goalTypeEnums.SKILL },
    { label: 'Parent Training', id: goalTypeEnums.PARENT_TRAINING },
];

export const teachingMethodEnums = {
    DISCRETE_TRIAL: 'DISCRETE_TRIAL',
    NATURALISTIC: 'NATURALISTIC',
    TASK_ANALYSIS: 'TASK_ANALYSIS',
    ERRORLESS_LEARNING: 'ERRORLESS_LEARNING',
    BEHAVIOR_SKILLS_TRAINING: 'BEHAVIOR_SKILLS_TRAINING',

    //     SHAPING: 'SHAPING',
    //     CHAINING: 'CHAINING',
    //     PROMPTING: 'PROMPTING',
    //     MODELING: 'MODELING',
    //     REINFORCEMENT: 'REINFORCEMENT',
    //     GENERALIZATION: 'GENERALIZATION',
    //     FUNCTIONAL_COMMUNICATION_TRAINING: 'FUNCTIONAL_COMMUNICATION_TRAINING',
};

export const teachingMethods = [
    { label: 'Discrete Trial', id: teachingMethodEnums.DISCRETE_TRIAL },
    { label: 'Naturalistic', id: teachingMethodEnums.NATURALISTIC },
    { label: 'Task Analysis', id: teachingMethodEnums.TASK_ANALYSIS },
    { label: 'Errorless Learning', id: teachingMethodEnums.ERRORLESS_LEARNING },
    { label: 'Behavior Skills Training(BST)', id: teachingMethodEnums.BEHAVIOR_SKILLS_TRAINING },

    // { label: 'Shaping', id: teachingMethodEnums.SHAPING },
    // { label: 'Chaining', id: teachingMethodEnums.CHAINING },
    // { label: 'Prompting', id: teachingMethodEnums.PROMPTING },
    // { label: 'Modeling', id: teachingMethodEnums.MODELING },
    // { label: 'Reinforcement', id: teachingMethodEnums.REINFORCEMENT },
    // { label: 'Generalization', id: teachingMethodEnums.GENERALIZATION },
    // { label: 'Functional Communication Training', id: teachingMethodEnums.FUNCTIONAL_COMMUNICATION_TRAINING },
];

export const TAModeEnums = {
    ALL: 'ALL',
    BACKWARD_CHAINING: 'BACKWARD_CHAINING',
    FORWARD_CHAINING: 'FORWARD_CHAINING',
    TOTAL_TASK_CHAINING: 'TOTAL_TASK_CHAINING',
};

export const TAModes = [
    { label: 'All', id: TAModeEnums.ALL },
    { label: 'Backward Chaining', id: TAModeEnums.BACKWARD_CHAINING },
    { label: 'Forward Chaining', id: TAModeEnums.FORWARD_CHAINING },
    { label: 'Total Task Chaining', id: TAModeEnums.TOTAL_TASK_CHAINING },
];
export const convertGoalEnums = (type) => {
    switch (type) {
        case goalTypeEnums.BEHAVIOR:
            return 'Behavior';
        case goalTypeEnums.SKILL:
            return 'Skill';
        case goalTypeEnums.PARENT_TRAINING:
            return 'Parent Training';
        case teachingMethodEnums.DISCRETE_TRIAL:
            return 'Discrete Trial';
        case teachingMethodEnums.NATURALISTIC:
            return 'Naturalistic';
        // case teachingMethodEnums.SHAPING:
        //     return 'Shaping';
        // case teachingMethodEnums.CHAINING:
        //     return 'Chaining';
        // case teachingMethodEnums.PROMPTING:
        //     return 'Prompting';
        // case teachingMethodEnums.MODELING:
        //     return 'Modeling';
        // case teachingMethodEnums.REINFORCEMENT:
        //     return 'Reinforcement';
        // case teachingMethodEnums.GENERALIZATION:
        //     return 'Generalization';
        case teachingMethodEnums.ERRORLESS_LEARNING:
            return 'Errorless Learning';
        case teachingMethodEnums.BEHAVIOR_SKILLS_TRAINING:
            return 'Behavior Skills Training(BST)';
        case teachingMethodEnums.TASK_ANALYSIS:
            return 'Task Analysis';
        // case teachingMethodEnums.FUNCTIONAL_COMMUNICATION_TRAINING:
        //     return 'Functional Communication Training';
        case TAModeEnums.BACKWARD_CHAINING:
            return 'Backward Chaining';
        case TAModeEnums.FORWARD_CHAINING:
            return 'Forward Chaining';
        case TAModeEnums.TOTAL_TASK_CHAINING:
            return 'Total Task Chaining';
        case TAModeEnums.ALL:
            return 'All';

        default:
            return type;
    }
};

/** Maintenance badge ("Maintenance" / "Maintenance completed") and details of a mastered target. */
const TargetMaintenanceBadges = ({ target, goal }) => {
    if (!isMaintenanceRunning(target) && !isMaintenanceCompleted(target)) return null;
    return (
        <>
            {isMaintenanceRunning(target) ? (
                <StatusCard status={maintenanceBadges.RUNNING.label} custom={maintenanceBadges.RUNNING} />
            ) : (
                <StatusCard status={maintenanceBadges.COMPLETED.label} custom={maintenanceBadges.COMPLETED} />
            )}
            <MaintenanceInfo target={target} goal={goal} config={target?.maintenance?.followGoalSettings ? goal?.maintenance : target?.maintenance} />
        </>
    );
};
